import { supabase, isSupabaseConfigured } from './supabaseClient';
import { getMergeRelatedOfferConfigsForSlug } from '../config/productRelations';
import { getPriceLookupIdForSimpleProductSlug } from './productUrlMapping';
import { selectCurrentVerifiedBest, sortCustomerOffers } from '../domain/offerFreshness';
import { getOfferLink } from '../components/PriceComparison/simpleProductPageShared';

// Simple in-memory cache to prevent duplicate requests
const priceCache = new Map();
const CACHE_DURATION = 30000; // 30 seconds

// Feedback submission function
export const submitFeedback = async (comment, sessionDetails) => {
  // Debug logs
  console.log('Submit Feedback - Session Details:', sessionDetails);
  
  // Format bundle details in a readable way
  const formatBundleDetails = (details) => {
    // Check if we have any valid component
    const hasAnyComponent = 
      details?.selectedScreen?.name || 
      details?.selectedTransducer?.name || 
      details?.selectedBattery?.name || 
      details?.selectedShuttle?.name || 
      details?.selectedPole?.name;
    
    if (!hasAnyComponent) {
      console.log('No components found in session details');
      return '\n\nNo bundle configured yet';
    }

    let bundleText = '\n\nbundleDetails\n\n';
    
    // Add each component if it exists
    if (details?.selectedScreen?.name) {
      bundleText += `Screen: ${details.selectedScreen.name}\n`;
    }
    
    if (details?.selectedTransducer?.name) {
      bundleText += `Transducer: ${details.selectedTransducer.name}\n`;
    }
    
    if (details?.selectedBattery?.name) {
      bundleText += `Battery: ${details.selectedBattery.name}\n`;
    }
    
    if (details?.selectedShuttle?.name) {
      bundleText += `Shuttle: ${details.selectedShuttle.name}\n`;
    }
    
    if (details?.selectedPole?.name) {
      bundleText += `Pole: ${details.selectedPole.name}\n`;
    }
    
    // Add total MSRP if available
    if (details?.totalMSRP) {
      bundleText += `Total MSRP: $${details.totalMSRP.toFixed(2)}`;
    }

    console.log('Formatted bundle text:', bundleText);
    return bundleText;
  };

  const formattedMessage = comment + formatBundleDetails(sessionDetails);
  console.log('Sending formatted message:', formattedMessage);

  const response = await fetch('https://formspree.io/f/mgvaevdy', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      message: formattedMessage
    })
  });

  if (!response.ok) {
    throw new Error('Failed to submit feedback');
  }

  return response;
};

// ---------------------------------------------------------------------------
// Price fetch debug (set to false to silence; remove block when done debugging)
// ---------------------------------------------------------------------------
const DEBUG_PRICES = typeof process.env.REACT_APP_DEBUG_PRICES !== 'undefined'
  ? process.env.REACT_APP_DEBUG_PRICES === 'true'
  : false;
function debugPrices(...args) {
  if (DEBUG_PRICES) {
    console.log('[prices]', ...args);
  }
}

// ---------------------------------------------------------------------------
// Lookup mapping: frontend csvLookupId / productId -> latest_prices_view.product_name
// Only add entries where Supabase product_name DIFFERS from the frontend string.
// 1) Run in Supabase: SELECT DISTINCT product_name FROM latest_prices_view ORDER BY product_name;
// 2) For each frontend string that does NOT appear in that result, add: 'frontend': 'exact Supabase value'
// 3) Remove any line below if your Supabase uses the frontend string as-is (no mapping needed).
// Full list of frontend strings: docs/SUPABASE_PRICE_LOOKUP_STRINGS.md
// ---------------------------------------------------------------------------
const LOOKUP_TO_SUPABASE_PRODUCT_NAME = {
  // --- Add only mismatches. Right-hand side must exist in latest_prices_view. ---
  // Example: if Supabase has "Garmin LiveScope Plus LVS34" but frontend sends "LiveScope Plus LVS34":
  // 'LiveScope Plus LVS34': 'Garmin LiveScope Plus LVS34',
  // Example: if Supabase has "Amped Outdoors 30Ah LiFePO4 + 3A Charger":
  // '30Ah LiFePO4 + 3A Charger': 'Amped Outdoors 30Ah LiFePO4 + 3A Charger',
  'LiveScope 2 HD LVS42HD-IF':
    'Garmin LiveScope 2 HD Ice Fishing Transducer LVS42HD-IF',
};

export function resolveProductNameForSupabase(lookupId) {
  if (!lookupId || typeof lookupId !== 'string') return lookupId;
  const trimmed = lookupId.trim();
  return LOOKUP_TO_SUPABASE_PRODUCT_NAME[trimmed] ?? trimmed;
}

/**
 * Fetch the set of product_name values that have at least one active offer in latest_prices_view.
 * Used by the Price Comparison Hub to hide cards for products with no offers (e.g. Amped-only after deactivation).
 */
export const fetchProductNamesWithOffers = async () => {
  if (!isSupabaseConfigured()) return new Set();
  try {
    const { data: rows, error } = await supabase
      .from('latest_prices_view')
      .select('product_name');
    if (error) {
      debugPrices('fetchProductNamesWithOffers error', error.message);
      return new Set();
    }
    const names = [...new Set((rows || []).map((r) => r.product_name).filter(Boolean))];
    return new Set(names);
  } catch (err) {
    debugPrices('fetchProductNamesWithOffers error', err?.message);
    return new Set();
  }
};

/**
 * Normalize price from Supabase (number or string) to UI format.
 * Returns { priceStr, numericPrice } for use in processed row.
 */
function normalizePrice(price) {
  if (price == null || price === '') {
    return { priceStr: 'N/A', numericPrice: 0 };
  }
  const num = typeof price === 'number' ? price : parseFloat(String(price).replace(/[$,]/g, '') || '0');
  const numericPrice = isNaN(num) ? 0 : num;
  const priceStr = typeof price === 'number' ? `$${price.toFixed(2)}` : (price || 'N/A');
  return { priceStr, numericPrice };
}

/**
 * Map a raw latest_prices_view row to the UI-friendly shape expected by SimplePriceComparison.
 * Used by getOffersByProductId and keeps the same row shape as fetchProductPricesByLookup output.
 */
function mapLatestPricesRowToUI(row) {
  const priceValue = row.current_price != null ? row.current_price : row.price;
  const { priceStr, numericPrice } = normalizePrice(priceValue);
  const lastSuccess = row.last_success_at || null;
  const lastChecked = row.last_checked_at || null;
  const affiliateUrl = row.affiliate_url || row.affiliate_link || '';
  return {
    Price: priceStr,
    Product: row.product_name || 'Unknown Product',
    'Date Time Stamp': lastSuccess || lastChecked || '',
    Source: row.website || 'Unknown',
    'Job Link': affiliateUrl,
    numeric_price: numericPrice,
    current_price: priceValue ?? null,
    list_price: row.list_price ?? null,
    variant_label: row.variant_label ?? null,
    affiliate_url: affiliateUrl,
    last_success_at: lastSuccess,
    last_checked_at: lastChecked,
    availability_status: row.availability_status || null,
  };
}

// =============================================================================
// DB-driven product/offers API (slug -> products -> product_id -> latest_prices_view)
// Use these for the new reusable product comparison page; do not use product_name
// as the primary path for the DB-driven flow.
// =============================================================================

// TEMPORARY: Slug alias map so legacy URLs resolve to the canonical product row in DB.
// Remove when products table has a single canonical row per product (e.g. slug cleanup/merge).
const SLUG_ALIAS_TO_CANONICAL = {
  'garmin-livescope-plus-lvs34': 'livescope-plus-lvs34',
  'garmin-livescope-plus-system': 'livescope-plus-lvs34',
};

/**
 * Fetch a single product by slug from the products table.
 *
 * @param {string} slug - URL slug (e.g. "livescope-plus-lvs34")
 * @returns {Promise<{ id, name, slug, image_url, msrp, ... } | null>}
 *   Product row, or null if not found or Supabase not configured.
 *   Throws on Supabase error (e.g. network). PGRST116 (no rows) returns null.
 */
export const getProductBySlug = async (slug) => {
  if (!slug || typeof slug !== 'string') return null;
  const trimmed = slug.trim();
  if (!trimmed) return null;
  if (!isSupabaseConfigured()) {
    debugPrices('getProductBySlug: Supabase not configured');
    return null;
  }
  // Resolve alias so legacy slugs (e.g. livescope-plus-lvs34) hit the canonical row
  const querySlug = SLUG_ALIAS_TO_CANONICAL[trimmed] ?? trimmed;
  try {
    const { data, error } = await supabase
      .from('products')
      // Keep this column list to fields that exist before Product Source of Truth v1
      // is applied. After the migration, entity_kind / recommendation_status /
      // lifecycle_status / model_number may be selected in a follow-up.
      .select('id, brand, name, slug, category, msrp, upc, mpn, image_url, is_active')
      .eq('slug', querySlug)
      .maybeSingle();
    if (error) {
      debugPrices('getProductBySlug error', error.message);
      throw new Error(error.message || 'Failed to fetch product by slug');
    }
    return data || null;
  } catch (err) {
    debugPrices('getProductBySlug error', err?.message);
    throw err;
  }
};

const SCREEN_LANDING_SPEC_KEYS = [
  'display_diagonal_inches_exact',
  'touch_supported',
  'keypad_supported',
];

function pickSpecValueForLanding(row) {
  if (row.spec_value_numeric !== null && row.spec_value_numeric !== undefined) {
    return row.spec_value_numeric;
  }
  if (row.spec_value_boolean !== null && row.spec_value_boolean !== undefined) {
    return row.spec_value_boolean;
  }
  if (row.spec_value_text !== null && row.spec_value_text !== undefined) {
    return row.spec_value_text;
  }
  return null;
}

/**
 * Screen products for the price-comparison landing grid from Supabase (`products` + `product_specs`).
 * Maps each row to the legacy static shape: `id` = slug, `csvLookupId` from {@link getPriceLookupIdForSimpleProductSlug}.
 *
 * @returns {Promise<Array<{
 *   id: string, name: string, msrp: number|null, image: string, fallbackImage: string,
 *   csvLookupId: string|null, screenSize: string, controlMethod: string,
 *   category: 'Screens', categorySlug: 'screen', priceComparisonUrl: string, searchTerms: string
 * }>>}
 */
export async function fetchScreenProductsForPriceComparisonLanding() {
  if (!isSupabaseConfigured()) {
    return [];
  }
  try {
    const { data: products, error } = await supabase
      .from('products')
      .select('id, slug, name, msrp, image_url')
      .in('category', ['screen', 'screens'])
      .eq('is_active', true)
      .not('slug', 'is', null);

    if (error) {
      debugPrices('fetchScreenProductsForPriceComparisonLanding products', error.message);
      return [];
    }

    const list = Array.isArray(products) ? products.filter((p) => p.slug && String(p.slug).trim()) : [];
    console.log('[price-comparison][fetchScreenProducts] products row count', list.length, 'slugs', list.map((p) => p.slug));
    if (list.length === 0) {
      return [];
    }

    const ids = list.map((p) => p.id);
    const { data: specRows, error: specErr } = await supabase
      .from('product_specs')
      .select('product_id, spec_key, spec_value_numeric, spec_value_boolean, spec_value_text')
      .in('product_id', ids)
      .in('spec_key', SCREEN_LANDING_SPEC_KEYS);

    if (specErr) {
      debugPrices('fetchScreenProductsForPriceComparisonLanding specs', specErr.message);
    }

    const specsByPid = new Map();
    for (const row of specRows || []) {
      if (!SCREEN_LANDING_SPEC_KEYS.includes(row.spec_key)) continue;
      if (!specsByPid.has(row.product_id)) specsByPid.set(row.product_id, {});
      const m = specsByPid.get(row.product_id);
      m[row.spec_key] = pickSpecValueForLanding(row);
    }

    return list.map((p) => {
      const specs = specsByPid.get(p.id) || {};
      const inches = specs.display_diagonal_inches_exact;
      const n = inches != null ? Number(inches) : NaN;
      const screenSize = Number.isFinite(n) ? String(n) : '';

      let controlMethod = '-';
      if (specs.touch_supported === true) controlMethod = 'Touchscreen';
      else if (specs.keypad_supported === true) controlMethod = 'Keypad';

      const slug = String(p.slug).trim();
      const csvLookupId = getPriceLookupIdForSimpleProductSlug(slug);
      const msrpNum =
        p.msrp !== null && p.msrp !== undefined && p.msrp !== ''
          ? Number(p.msrp)
          : null;

      const name = p.name ?? '';
      const searchTerms = `${name} ${screenSize ? `${screenSize}"` : ''} screen fish finder display`.toLowerCase();

      return {
        id: slug,
        name,
        msrp: msrpNum != null && !Number.isNaN(msrpNum) ? msrpNum : null,
        image: p.image_url || '',
        fallbackImage: p.image_url || '',
        csvLookupId,
        screenSize,
        controlMethod,
        category: 'Screens',
        categorySlug: 'screen',
        priceComparisonUrl: `/simple-products/${slug}`,
        searchTerms,
      };
    });
  } catch (err) {
    debugPrices('fetchScreenProductsForPriceComparisonLanding', err?.message);
    return [];
  }
}

/**
 * Transducer products for the price-comparison landing grid from Supabase `products`.
 *
 * @returns {Promise<Array<{
 *   id: string, name: string, msrp: number|null, image: string, fallbackImage: string,
 *   csvLookupId: string|null, category: 'Transducers', categorySlug: 'transducer',
 *   priceComparisonUrl: string, searchTerms: string
 * }>>}
 */
export async function fetchTransducerProductsForPriceComparisonLanding() {
  if (!isSupabaseConfigured()) {
    return [];
  }
  try {
    const { data: products, error } = await supabase
      .from('products')
      .select('id, slug, name, msrp, image_url')
      .in('category', ['transducer', 'transducers'])
      .eq('is_active', true)
      .not('slug', 'is', null);

    if (error) {
      debugPrices('fetchTransducerProductsForPriceComparisonLanding products', error.message);
      return [];
    }

    const list = Array.isArray(products) ? products.filter((p) => p.slug && String(p.slug).trim()) : [];
    if (list.length === 0) {
      return [];
    }

    return list.map((p) => {
      const slug = String(p.slug).trim();
      const csvLookupId = getPriceLookupIdForSimpleProductSlug(slug);
      const msrpNum =
        p.msrp !== null && p.msrp !== undefined && p.msrp !== ''
          ? Number(p.msrp)
          : null;

      const name = p.name ?? '';
      const searchTerms = `${name} transducer sonar`.toLowerCase();

      return {
        id: slug,
        name,
        msrp: msrpNum != null && !Number.isNaN(msrpNum) ? msrpNum : null,
        image: p.image_url || '',
        fallbackImage: p.image_url || '',
        csvLookupId,
        category: 'Transducers',
        categorySlug: 'transducer',
        priceComparisonUrl: `/simple-products/${slug}`,
        searchTerms,
      };
    });
  } catch (err) {
    debugPrices('fetchTransducerProductsForPriceComparisonLanding', err?.message);
    return [];
  }
}

/**
 * Fetch all offers for a product from latest_prices_view by product_id, ordered by price ascending.
 * Returns the same UI-friendly shape as fetchProductPricesByLookup so SimplePriceComparison can use it.
 *
 * @param {string} productId - UUID (or id) of the product from the products table
 * @returns {Promise<{ success: boolean, data: Array<{ Price, Product, 'Date Time Stamp', Source, 'Job Link', numeric_price }>, source: string }>}
 *   success: true and data array (possibly empty); source: 'supabase'. Never uses fallback data.
 */
export const getOffersByProductId = async (productId) => {
  if (!productId) {
    return { success: true, data: [], source: 'supabase' };
  }
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase is not configured (REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY)');
  }
  try {
    const { data: rows, error } = await supabase
      .from('latest_prices_view')
      .select('product_id, product_name, website, current_price, list_price, variant_label, affiliate_url, last_checked_at, last_success_at, availability_status')
      .eq('product_id', productId)
      .order('current_price', { ascending: true });
    if (error) {
      debugPrices('getOffersByProductId error', error.message);
      throw new Error(error.message || 'Failed to fetch offers by product_id');
    }
    const processedData = (Array.isArray(rows) ? rows : []).map(mapLatestPricesRowToUI);
    debugPrices('getOffersByProductId', productId, 'rows:', processedData.length);
    return {
      success: true,
      data: processedData,
      source: 'supabase'
    };
  } catch (err) {
    debugPrices('getOffersByProductId error', err?.message);
    throw err;
  }
};

function filterValidPricedOffersForMerge(offers) {
  if (!Array.isArray(offers)) return [];
  return offers.filter(
    (item) =>
      typeof item?.numeric_price === 'number' &&
      Number.isFinite(item.numeric_price) &&
      item.numeric_price > 0
  );
}

/**
 * For configured main PDP slugs only: load related product offers (by product_id),
 * label them for display, and merge into the main offer list sorted by price.
 * Does not change Supabase data.
 *
 * @param {string} mainSlug
 * @param {Array} mainOffers - UI-shaped offers for the main product
 * @returns {Promise<Array>}
 */
export const mergeRelatedOffersIntoDisplayList = async (mainSlug, mainOffers) => {
  const configs = getMergeRelatedOfferConfigsForSlug(mainSlug);
  const main = Array.isArray(mainOffers) ? [...mainOffers] : [];
  if (!configs.length) return main;

  const mergedRelated = [];

  for (const config of configs) {
    const product = await getProductBySlug(config.slug);
    if (!product) continue;

    const offersResult = await getOffersByProductId(product.id);
    const validOffers = filterValidPricedOffersForMerge(offersResult?.data);

    validOffers.forEach((offer, index) => {
      const retailer = offer.Source || 'Unknown';
      mergedRelated.push({
        ...offer,
        logoRetailer: retailer,
        sourceDisplay: config.sourceDisplay || retailer,
        variantInfo: Array.isArray(config.variantInfo) ? config.variantInfo : null,
        offerKey: `merged-${config.slug}-${retailer}-${index}-${offer['Job Link'] || ''}`,
        isMergedRelatedOffer: true,
      });
    });
  }

  return sortCustomerOffers([...main, ...mergedRelated]);
};

/**
 * Fetch the best current price from the same verified retailer offers as the product page.
 * Uses the same current-price eligibility and retailer ordering as product pages.
 *
 * @param {string} productId - UUID (or id) of the product from the products table
 * @returns {Promise<{ success: boolean, lowestPrice: number | null, retailer: string | null, affiliate_url: string | null } | null>}
 *   Best current price summary, or null when no eligible quote is available.
 */
export const getBestPriceByProductId = async (productId) => {
  if (!productId || !isSupabaseConfigured()) return null;
  try {
    const result = await getOffersByProductId(productId);
    const best = selectCurrentVerifiedBest(result?.data || []);
    if (!best.hasCurrent) return null;
    return { success: true, lowestPrice: best.lowestPrice, retailer: best.primary.Source,
      affiliate_url: getOfferLink(best.primary) };
  } catch (err) {
    debugPrices('getBestPriceByProductId error', err?.message);
    return null;
  }
};

/**
 * Fetch latest prices from Supabase `latest_prices_view` by product_name (lookup id string).
 * Maps view columns to the shape the UI expects (Product, Price, Source, Job Link, etc.).
 * No-match: returns { success: true, data: [] } (graceful). No current prices are synthesized.
 */
export const fetchProductPricesByLookup = async (productId = null, csvLookupId = null, exactMatchOnly = false) => {
  const lookupId = csvLookupId || productId;
  debugPrices('fetchProductPricesSupabase', { productId, csvLookupId, lookupId, exactMatchOnly });

  try {
    if (!lookupId) {
      throw new Error('No product ID or csvLookupId provided');
    }

    const cacheKey = `${lookupId}_${exactMatchOnly}`;
    const cachedData = priceCache.get(cacheKey);
    if (cachedData && Date.now() - cachedData.timestamp < CACHE_DURATION) {
      debugPrices('cache hit', lookupId, 'items:', cachedData.data?.data?.length ?? 0);
      return cachedData.data;
    }

    const timestamp = Date.now();

    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured (REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY)');
    }

    const productNameForQuery = resolveProductNameForSupabase(lookupId);
    debugPrices('Supabase query product_name:', productNameForQuery);

    // View may expose price as "price" or "current_price" (from product_offers)
    const { data: rows, error } = await supabase
      .from('latest_prices_view')
      .select('product_name, website, current_price, list_price, variant_label, affiliate_url, last_checked_at, last_success_at, availability_status')
      .eq('product_name', productNameForQuery);

    if (error) {
      debugPrices('Supabase error', error.message, error);
      throw new Error(error.message || 'Failed to fetch prices from Supabase');
    }

    const rowCount = Array.isArray(rows) ? rows.length : 0;
    debugPrices('Supabase row count for', productNameForQuery, '=', rowCount);

    const filteredData = Array.isArray(rows) ? rows : [];
    const usingFallback = false;

    const processedData = filteredData.map(mapLatestPricesRowToUI);

    debugPrices('returning', processedData.length, 'items for', lookupId);

    const result = {
      success: true,
      data: processedData,
      source: 'supabase',
      usingFallback,
      timestamp
    };

    priceCache.set(cacheKey, { data: result, timestamp: Date.now() });
    return result;
  } catch (error) {
    debugPrices('fetchProductPricesSupabase error', lookupId, error?.message);
    throw error;
  }
};

// Retry wrapper function
export const fetchProductPrices = async (productId = null, retryCount = 0, maxRetries = 4, csvLookupId = null, exactMatchOnly = false) => {
  const lookup = csvLookupId || productId;
  try {
    debugPrices('fetchProductPrices', lookup, `attempt ${retryCount + 1}/${maxRetries + 1}`);
    const result = await fetchProductPricesByLookup(productId, csvLookupId, exactMatchOnly);
    debugPrices('fetchProductPrices success', lookup, 'items:', result?.data?.length ?? 0);
    return result;
  } catch (error) {
    if (error.message?.includes('No pricing data found from server')) {
      debugPrices('fetchProductPrices no data (expected for some products)', lookup);
      throw error;
    }
    debugPrices('fetchProductPrices error', lookup, error?.message);
    if (retryCount < maxRetries) {
      const retryDelay = Math.min(2000 + (retryCount * 1000), 8000);
      debugPrices('fetchProductPrices retry in', retryDelay, 'ms');
      await new Promise(resolve => setTimeout(resolve, retryDelay));
      return fetchProductPrices(productId, retryCount + 1, maxRetries, csvLookupId, exactMatchOnly);
    }
    throw error;
  }
};

// Lowest CURRENT VERIFIED purchasable price. Stale offers cannot populate this.
export const fetchLowestPrice = async (csvLookupId, exactMatchOnly = false, msrp = null, isAmpedOutdoors = false) => {
  try {
    debugPrices('fetchLowestPrice', csvLookupId, { isAmpedOutdoors });
    const result = await fetchProductPrices(null, 0, 1, csvLookupId, exactMatchOnly);
    const offers = Array.isArray(result?.data) ? result.data : [];
    const selection = selectCurrentVerifiedBest(offers, { isAmpedOutdoors });

    if (selection.hasCurrent && selection.lowestPrice != null) {
      const lowestPrice = parseFloat(selection.lowestPrice.toFixed(2));
      let msrpSavings = null;
      if (msrp && typeof msrp === 'number' && msrp > lowestPrice) {
        msrpSavings = { amount: (msrp - lowestPrice).toFixed(2), reference: 'MSRP' };
      }
      return {
        success: true,
        currentVerified: true,
        price: lowestPrice,
        lowestPrice,
        isTie: selection.isTie,
        data: offers,
        source: result.source,
        msrpSavings,
      };
    }

    debugPrices('fetchLowestPrice no current verified', csvLookupId);
    return {
      success: false,
      currentVerified: false,
      price: null,
      lowestPrice: null,
      data: offers,
      source: offers.length ? 'no-current-verified' : 'no-data',
    };
  } catch (error) {
    debugPrices('fetchLowestPrice error', csvLookupId, error?.message);
    return {
      success: false,
      currentVerified: false,
      lowestPrice: null,
      price: null,
      data: [],
      source: 'error',
      error: error.message
    };
  }
}; 